import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { CandidateAttachmentsData, JobAttachmentsData } from '../../../interfaces';
import { GET_CANDIDATE_ATTACHMENTS, GET_JOB_ATTACHMENTS } from '../../../GraphQL/attachments/queries';
import AttachmentItem from '../AttachmentItem';

interface AttachmentListProps {
  entityId: number;
  type: 'candidate' | 'job';
  onAttachmentDeleted?: () => void;
}

const AttachmentList: React.FC<AttachmentListProps> = ({ entityId, type, onAttachmentDeleted }) => {
  const query = type === 'candidate' ? GET_CANDIDATE_ATTACHMENTS : GET_JOB_ATTACHMENTS;
  const variables = type === 'candidate' ? { candidateId: entityId } : { jobId: entityId };

  const { data, loading, error, refetch } = useQuery<
    CandidateAttachmentsData | JobAttachmentsData
  >(query, {
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const handleAttachmentDeleted = () => {
    refetch();
    onAttachmentDeleted?.();
  };

  if (loading) {
    return (
      <div className="flex justify-center p-3">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Error loading attachments: {error.message}</AlertDescription>
      </Alert>
    );
  }

  const attachments = type === 'candidate'
    ? (data as CandidateAttachmentsData)?.candidateAttachments || []
    : (data as JobAttachmentsData)?.jobAttachments || [];

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div>
      {attachments.map((attachment) => (
        <AttachmentItem
          key={attachment.id}
          attachment={attachment}
          type={type}
          onDelete={handleAttachmentDeleted}
        />
      ))}
    </div>
  );
};

export default AttachmentList;